/**
 * TokenUsageIndicator — compact display of token usage and cost for a chat message.
 *
 * Shows a collapsible summary line below assistant messages:
 *   "1.2K tokens · $0.003 · Sonnet 4.5"
 *
 * Expands on click to show input/output/cache breakdown.
 * Visibility controlled by localStorage preference + platform setting.
 */

import { useState } from "react";
import { Zap, ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { TokenUsage } from "@/api/services";
import { MODEL_DISPLAY_NAMES } from "@/api/services";

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return `${n}`;
}

function formatCost(usd: number): string {
  if (usd === 0) return "$0.00";
  if (usd < 0.001) return "<$0.001";
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(3)}`;
}

function getModelShortName(modelId: string): string {
  return MODEL_DISPLAY_NAMES[modelId] || modelId.replace("claude-", "").replace(/-\d+$/, "");
}

interface TokenUsageIndicatorProps {
  usage: TokenUsage;
  className?: string;
}

export function TokenUsageIndicator({ usage, className }: TokenUsageIndicatorProps) {
  const [expanded, setExpanded] = useState(false);

  if (!usage || usage.total_tokens === 0) return null;

  const modelName = getModelShortName(usage.model);

  return (
    <div className={cn("mt-1", className)}>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-md px-2 py-0.5",
          "text-xs text-slate-600 dark:text-slate-300 hover:text-slate-600 dark:hover:text-slate-300",
          "hover:bg-slate-50/60 dark:bg-slate-800/40 transition-colors",
          "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-border-focus",
        )}
      >
        <Zap className="h-3 w-3 text-amber-500/70" />
        <span>{formatTokens(usage.total_tokens)} tokens</span>
        <span className="text-text-quaternary">·</span>
        <span>{formatCost(usage.cost_usd)}</span>
        {modelName && (
          <>
            <span className="text-text-quaternary">·</span>
            <span>{modelName}</span>
          </>
        )}
        {expanded ? (
          <ChevronDown className="h-2.5 w-2.5 ml-0.5" />
        ) : (
          <ChevronRight className="h-2.5 w-2.5 ml-0.5" />
        )}
      </button>

      {expanded && (
        <div className="mt-1 ml-2 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/30 px-3 py-2 text-xs text-slate-600 dark:text-slate-300 space-y-0.5">
          <div className="flex justify-between gap-8">
            <span>Input tokens</span>
            <span className="font-mono">{usage.input_tokens.toLocaleString()}</span>
          </div>
          <div className="flex justify-between gap-8">
            <span>Output tokens</span>
            <span className="font-mono">{usage.output_tokens.toLocaleString()}</span>
          </div>
          {usage.cache_read_tokens > 0 && (
            <div className="flex justify-between gap-8">
              <span>Cache read</span>
              <span className="font-mono">{usage.cache_read_tokens.toLocaleString()}</span>
            </div>
          )}
          {usage.cache_creation_tokens > 0 && (
            <div className="flex justify-between gap-8">
              <span>Cache write</span>
              <span className="font-mono">{usage.cache_creation_tokens.toLocaleString()}</span>
            </div>
          )}
          <div className="flex justify-between gap-8 border-t border-slate-200 dark:border-slate-700 pt-0.5 mt-0.5 font-medium text-slate-600 dark:text-slate-300">
            <span>Total cost</span>
            <span className="font-mono">{formatCost(usage.cost_usd)}</span>
          </div>
          <div className="flex justify-between gap-8">
            <span>Model</span>
            <span>{modelName}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default TokenUsageIndicator;
